import Papa from 'papaparse';

import tenants from '~/consts/tenant';

import type { TranslationCatalog, TranslationCatalogPatch, TranslationCatalogRow } from './types';

const DEFAULT_FIELD_DESCRIPTIONS: Record<string, string> = {
  t_subject_line: 'Subject line for the email. Do not translate anything inside {{...}} ${...} or {%...%} tags.',
  t_preheader:
    'Preview text shown below the subject line in the inbox. Do not translate anything inside {{...}} ${...} or {%...%} tags.',
  t_email_header_text_1:
    'Main header text at the top of the email body. Do not translate anything inside {{...}} ${...} or {%...%} tags.',
  t_email_body_text_1:
    'Main body paragraph below the header. May contain HTML like <br/>. Do not translate anything inside {{...}} ${...} or {%...%} tags.',
  t_email_header_text_2:
    'Section header introducing the brand value proposition. Do not translate anything inside {{...}} ${...} or {%...%} tags.',
  t_email_cta_text_1:
    'Primary call to action button label. Keep short and action-oriented. Do not translate anything inside {{...}} ${...} or {%...%} tags.',
  t_email_header_text_3:
    'Re-engagement section header. Do not translate anything inside {{...}} ${...} or {%...%} tags.',
  t_comp_header_text_1:
    'First value proposition header. Do not translate anything inside {{...}} ${...} or {%...%} tags.',
  t_comp_header_text_2:
    'Second value proposition header. Do not translate anything inside {{...}} ${...} or {%...%} tags.',
  t_comp_header_text_3:
    'Third value proposition header. Do not translate anything inside {{...}} ${...} or {%...%} tags.',
  t_comp_body_text_1:
    'First value proposition body copy. Do not translate anything inside {{...}} ${...} or {%...%} tags.',
  t_comp_body_text_2:
    'Second value proposition body copy. Do not translate anything inside {{...}} ${...} or {%...%} tags.',
  t_comp_body_text_3:
    'Third value proposition body copy. Do not translate anything inside {{...}} ${...} or {%...%} tags.',
};

export const DEFAULT_TRANSLATION_CATALOG_COLUMNS = Object.keys(DEFAULT_FIELD_DESCRIPTIONS);

/** Tenants whose Braze catalogs are translated, in the order they should be offered. */
export const TRANSLATION_CATALOG_TENANT_KEYS = ['lux', 'cudo', 'scoopon', 'deals', 'treatme', 'leagenthub'];

export const TRANSLATION_CATALOG_BRANDS = TRANSLATION_CATALOG_TENANT_KEYS.map((tenantKey) => tenants[tenantKey].brand);

const CSV_KEY_COLUMN = 'key';
const CSV_DESCRIPTION_COLUMN = 'description';
const CSV_SOURCE_COLUMN = 'en-AU';
const LOCALE_COLUMN_PATTERN = /^[a-z]{2}-[A-Z]{2}$/;

interface TranslationCatalogCsvParseResult {
  rows: Array<TranslationCatalogRow>;
  locales: Array<string>;
  rejectedHeaders: Array<string>;
  hasDescriptionColumn: boolean;
  hasSourceValueColumn: boolean;
}

export function getTranslationLocales(catalog?: TranslationCatalog | null): Array<string> {
  if (!catalog?.translatedValues) {
    return [];
  }

  return Object.keys(catalog.translatedValues).sort((a, b) => a.localeCompare(b));
}

export function buildTranslationCatalogRows(catalog: TranslationCatalog): Array<TranslationCatalogRow> {
  const locales = getTranslationLocales(catalog);

  return catalog.columnNames.map((key) => {
    const translations = locales.reduce<Record<string, string>>((result, locale) => {
      result[locale] = catalog.translatedValues[locale]?.[key] ?? '';
      return result;
    }, {});

    return {
      id: key,
      key,
      description: catalog.descriptions?.[key] ?? '',
      sourceValue: catalog.sourceValues?.[key] ?? '',
      translations,
    };
  });
}

export function mapRowsToTranslationCatalogPatch(
  rows: Array<TranslationCatalogRow>,
  locales: Array<string>,
): Pick<TranslationCatalogPatch, 'columnNames' | 'descriptions' | 'sourceValues' | 'translatedValues'> {
  const columnNames = rows.map((row) => row.key);
  const descriptions: Record<string, string> = {};
  const sourceValues: Record<string, string> = {};
  const translatedValues: Record<string, Record<string, string>> = locales.reduce(
    (result, locale) => {
      result[locale] = {};
      return result;
    },
    {} as Record<string, Record<string, string>>,
  );

  rows.forEach((row) => {
    descriptions[row.key] = row.description;
    sourceValues[row.key] = row.sourceValue;
    locales.forEach((locale) => {
      translatedValues[locale][row.key] = row.translations[locale] ?? '';
    });
  });

  return {
    columnNames,
    descriptions,
    sourceValues,
    translatedValues,
  };
}

export function getDefaultTranslationCatalogDescription(columnName: string): string {
  const normalizedColumnName = columnName.trim().toLowerCase();
  const exactDescription = DEFAULT_FIELD_DESCRIPTIONS[normalizedColumnName];

  if (exactDescription) {
    return exactDescription;
  }

  const sectionMatch = normalizedColumnName.match(/^t_section_(header|copy)_(\d+)$/);
  if (sectionMatch) {
    const [, fieldType, sectionNumber] = sectionMatch;
    return fieldType === 'header' ? `Section ${sectionNumber} heading.` : `Section ${sectionNumber} body copy.`;
  }

  return '';
}

export function buildTranslationCatalogDescriptions(columnNames: Array<string>): Record<string, string> {
  return columnNames.reduce<Record<string, string>>((values, columnName) => {
    values[columnName] = getDefaultTranslationCatalogDescription(columnName);
    return values;
  }, {});
}

export function parseTranslationCatalogCsv(csvText: string): TranslationCatalogCsvParseResult {
  const result = Papa.parse<Record<string, string>>(csvText, {
    delimiter: ',',
    header: true,
    skipEmptyLines: true,
    transformHeader: (header) => header.trim(),
  });

  if (result.errors.length > 0) {
    const firstError = result.errors[0];
    throw new Error(`Invalid CSV format: ${firstError.message}`);
  }

  const headers = result.meta.fields ?? [];
  if (headers[0] === 'id' && !headers.includes(CSV_KEY_COLUMN)) {
    return parseBrazeTranslationCatalogCsv(headers, result.data);
  }

  if (!headers.includes(CSV_KEY_COLUMN)) {
    throw new Error('CSV must include a "key" column.');
  }

  const locales = headers.filter((header) => header !== CSV_SOURCE_COLUMN && LOCALE_COLUMN_PATTERN.test(header));
  const acceptedHeaders = new Set([CSV_KEY_COLUMN, CSV_DESCRIPTION_COLUMN, CSV_SOURCE_COLUMN, ...locales]);
  const rejectedHeaders = headers.filter((header) => !acceptedHeaders.has(header));
  const seenKeys = new Set<string>();
  const rows: Array<TranslationCatalogRow> = [];

  result.data.forEach((row, index) => {
    const key = row[CSV_KEY_COLUMN]?.trim();
    if (!key) {
      return;
    }

    if (seenKeys.has(key)) {
      throw new Error(`CSV contains duplicate key "${key}".`);
    }

    if (!/^[A-Za-z0-9_]+$/.test(key)) {
      throw new Error(`CSV row ${index + 2} has invalid key "${key}". Use letters, numbers, and underscores only.`);
    }

    seenKeys.add(key);
    rows.push({
      id: key,
      key,
      description: row[CSV_DESCRIPTION_COLUMN]?.trim() || getDefaultTranslationCatalogDescription(key),
      sourceValue: row[CSV_SOURCE_COLUMN] ?? '',
      translations: locales.reduce<Record<string, string>>((values, locale) => {
        values[locale] = row[locale] ?? '';
        return values;
      }, {}),
    });
  });

  if (rows.length === 0) {
    throw new Error('CSV must include at least one row with a key value.');
  }

  return {
    rows,
    locales,
    rejectedHeaders,
    hasDescriptionColumn: headers.includes(CSV_DESCRIPTION_COLUMN),
    hasSourceValueColumn: headers.includes(CSV_SOURCE_COLUMN),
  };
}

function parseBrazeTranslationCatalogCsv(
  headers: Array<string>,
  data: Array<Record<string, string>>,
): TranslationCatalogCsvParseResult {
  const columnNames = headers.slice(1).filter(Boolean);
  if (columnNames.length === 0) {
    throw new Error('Braze CSV must include at least one catalog field column.');
  }

  const invalidColumnName = columnNames.find((columnName) => !/^[A-Za-z0-9_]+$/.test(columnName));
  if (invalidColumnName) {
    throw new Error(`Braze CSV has invalid field "${invalidColumnName}". Use letters, numbers, and underscores only.`);
  }

  const descriptionRow = data.find((row) => row.id === '_description');
  const sourceRow = data.find((row) => row.id === CSV_SOURCE_COLUMN);
  if (!sourceRow) {
    throw new Error('Braze CSV must include an "en-AU" row.');
  }

  const locales = data
    .map((row) => row.id?.trim())
    .filter((id): id is string => !!id && id !== '_description' && id !== CSV_SOURCE_COLUMN)
    .filter((id) => LOCALE_COLUMN_PATTERN.test(id))
    .sort((a, b) => a.localeCompare(b));
  const localeRows = new Map(data.map((row) => [row.id, row]));

  const rows = columnNames.map<TranslationCatalogRow>((key) => ({
    id: key,
    key,
    description: descriptionRow?.[key]?.trim() || getDefaultTranslationCatalogDescription(key),
    sourceValue: sourceRow[key] ?? '',
    translations: locales.reduce<Record<string, string>>((values, locale) => {
      values[locale] = localeRows.get(locale)?.[key] ?? '';
      return values;
    }, {}),
  }));

  return {
    rows,
    locales,
    rejectedHeaders: [],
    hasDescriptionColumn: !!descriptionRow,
    hasSourceValueColumn: true,
  };
}

export function buildTranslationCatalogCsvRows(
  rows: Array<TranslationCatalogRow>,
  locales: Array<string>,
): Array<Record<string, string>> {
  return rows.map((row) => ({
    [CSV_KEY_COLUMN]: row.key,
    [CSV_DESCRIPTION_COLUMN]: row.description,
    [CSV_SOURCE_COLUMN]: row.sourceValue,
    ...locales.reduce<Record<string, string>>((values, locale) => {
      values[locale] = row.translations[locale] ?? '';
      return values;
    }, {}),
  }));
}

export function getTranslationCatalogCsvHeaders(locales: Array<string>): Array<string> {
  return [CSV_KEY_COLUMN, CSV_DESCRIPTION_COLUMN, CSV_SOURCE_COLUMN, ...locales];
}

export function formatNullableDateTime(value?: string | null): string {
  if (!value) {
    return '-';
  }

  return new Intl.DateTimeFormat('en-AU', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

export function isTranslationCatalogLocked(catalog?: TranslationCatalog | null): boolean {
  if (!catalog?.lockedAt) {
    return false;
  }

  if (!catalog.unlockedAt) {
    return true;
  }

  return new Date(catalog.lockedAt).getTime() > new Date(catalog.unlockedAt).getTime();
}

export function isTranslationCatalogStale(catalog?: TranslationCatalog | null): boolean {
  if (!catalog?.updatedAt || !catalog.translatedAt) {
    return false;
  }

  return new Date(catalog.updatedAt).getTime() > new Date(catalog.translatedAt).getTime();
}
